import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import intl from 'react-intl-universal';
import { fetchCaseDetail, saveCaseDetail, fetchCountryFormDetail, fetchBpfStatus } from 'src/services/cases.service';
import CasesFormDetailPres from 'src/ui/features/Cases/presentational/CasesFormDetailPres';
import { withSnackbar } from 'notistack';
import { transformFormValues, updateBodyClassState } from 'src/helpers';
import { updateCasePhase } from 'src/ui/features/Cases/state/casesActions';
import { BODY_CLASSES, CASE_DETAIL_URL, DASHBOARD_URL, SERVICE_TYPE } from 'src/constants/constants.js';
import { formatDateAndTime } from 'src/helpers/date';
import { DATE_REGEX } from 'src/constants/constants.js';
import { commonFormatDate, dateFormatEnUS } from 'src/helpers/commonFunctions.js';
import { formDataDifference } from 'src/helpers/formDataDifference.js';

class CasesFormDetailContainer extends React.Component {
  static propTypes = {
    casePhases: PropTypes.object.isRequired,
    updateCasePhase: PropTypes.func.isRequired,
  };

  constructor(props) {
    super(props);
    this.formQVData = {};
    this.qvDataLoaded = false;
  }

  state = {
    formDetails: {},
    qvFormDetails: {},
    casePhases: {},
    isEditable: false,
    isLoading: false,
    isLoadingQV: false,
    showModal: false,
    withInitValue: false,
    regKey: null,
    initialFormData: {},
  };

  componentDidMount() {
    this.getFormDetails();
    this.formValues = null;
    this.refreshBackground();
  }

  componentDidUpdate(prevProps) {
    const { casePhases } = this.props;

    if (casePhases && prevProps.casePhases !== casePhases) {
      this.setState({ casePhases });
    }
    this.refreshBackground();
  }

  componentWillUnmount() {
    this.refreshBackground();
  }

  refreshBackground = () => {
    const { history, isApplicant } = this.props;
    if (isApplicant) {
      let darkThemeRequiredPages = [DASHBOARD_URL, CASE_DETAIL_URL];
      let viewDetailUrlPattern = /viewdetail$/;
      let isViewDetailUrlPattern = viewDetailUrlPattern.test(history.location.pathname);
      if (isViewDetailUrlPattern || !darkThemeRequiredPages.some(x => history.location.pathname.startsWith(x))) {
        updateBodyClassState(BODY_CLASSES.CASES, true);
        updateBodyClassState(BODY_CLASSES.CASE_DETAILS_APPLICANT, false);
      } else {
        updateBodyClassState(BODY_CLASSES.CASES, false);
        updateBodyClassState(BODY_CLASSES.CASE_DETAILS_APPLICANT, true);
      }
    }
  };

  handleEdit = () => {
    this.setState({ isEditable: true, initialFormData: this.formData });
  };

  remapDisplayTextToValue = caseData => {
    caseData.dataRequests.forEach(x => {
      var findArray = caseData.case.lookUps[x.attributeName];
      var foundIdx = -1;
      if (findArray && findArray.findIndex(y => y.lookUpBindValue === x.value) === -1) {
        foundIdx = findArray.findIndex(y => y.displayText === x.value);
        if (foundIdx !== -1) {
          x.value = findArray[foundIdx].lookUpBindValue;
        }
      }
    });
  };

  internalTransformFormData = (someFormData, requestParams) => {
    const { formValues } = someFormData;
    const data = {
      entityId: this.props.caseId || this.props.casePowerAppsId,
      ...requestParams,
      ...transformFormValues(formValues),
      languageId: 1,
      pageSourceId: requestParams.sourceTableId,
    };
    this.remapDisplayTextToValue(data);
    return data;
  };

  handleSave = async () => {
    this.setState({ isLoading: true });

    this.formData.formValues.dataRequests =
      Object.keys(this.state.initialFormData).length > 0
        ? formDataDifference(this.formData.formValues.dataRequests, this.state.initialFormData.formValues.dataRequests)
        : this.formData.formValues.dataRequests;
    let result = null;
    const transformedData = this.internalTransformFormData(this.formData, this.state.formDetails);
    delete transformedData.case;
    result = await saveCaseDetail(transformedData);
    if (result && !result.error) {
      // reload the formDetails
      const { caseId, casePowerAppsId, enqueueSnackbar } = this.props;
      const validCaseId = caseId || casePowerAppsId;
      const detailsResult = await fetchCaseDetail(validCaseId);
      detailsResult.case.pageElements.forEach(item => (item.numberOfColumns = item.numberOfColumns === null ? null : 2));
      this.setState({ formDetails: detailsResult, showModal: false, isEditable: false, isLoading: false }, () => {
        enqueueSnackbar(intl.get('cases.details.messages.successMessage'), { variant: 'success' });
      });
    }
  };

  handleCancel = () => {
    this.setState({
      showModal: false,
      isEditable: false,
    });
  };

  toggleModal = status => {
    this.setState({
      showModal: status,
    });
  };

  handleFormChange = data => {
    this.formData = data;
  };

  handleQVFormChange = data => {
    this.formQVData = data;
  };

  getRecordID = async caseObj => {
    let countryMappingFieldFound = caseObj.pageElements.find(x => x.controlTypeName !== 'Header');
    return countryMappingFieldFound ? countryMappingFieldFound.value.split('|')[0] : null;
  };

  isAdditionalInfoSectionExist = detailsResult => {
    if (detailsResult === null || detailsResult.case === undefined || detailsResult.case === null) {
      return false;
    }
    return detailsResult.case.hasCountryServiceMapping;
  };

  getValidRecord = async detailsResult => {
    const servicesWithCountryMapping = [SERVICE_TYPE.SocialSecurity, SERVICE_TYPE.Immigration, SERVICE_TYPE.PostedWorker];

    if (!servicesWithCountryMapping.includes(detailsResult.case.eyService)) {
      return null;
    }
    const validRecord = await this.getRecordID(detailsResult.case);
    return validRecord;
  };

  getFormDetails = async () => {
    const {
      caseId,
      casePowerAppsId,
      updateAdditionalInfoFound,
      updateAdditionalInfoData,
      updateMainCaseInfoData,
      setCaseLoading,
      setBpfStatusInfo,
    } = this.props;

    const validCaseId = caseId || casePowerAppsId;
    setCaseLoading({ isLoading: true });
    let [detailsResult, bpfInfo] = await Promise.all([fetchCaseDetail(validCaseId), fetchBpfStatus(validCaseId)]);
    for (let i = 0; i < detailsResult.case.pageElements.length; i++) {
      detailsResult.case.pageElements[i].value = this.modifyDateFormat(detailsResult.case.pageElements[i].value);
    }
    if (detailsResult === null || detailsResult.case === undefined || detailsResult.case === null) {
      setBpfStatusInfo(null);
    } else {
      for (let i = 0; i < bpfInfo.pageElements.length; i++) {
        bpfInfo.pageElements[i].value = this.modifyDateFormat(bpfInfo.pageElements[i].value);
      }
      if (bpfInfo && bpfInfo.success) {
        setBpfStatusInfo(bpfInfo);
      } else {
        setBpfStatusInfo(null);
      }
    }

    // check if the case has related country specific tab
    const isAdditionalInfoSecExist = this.isAdditionalInfoSectionExist(detailsResult);
    let additionalInfoDetailUpdateInfo = {};

    if (isAdditionalInfoSecExist) {
      const validRecord = await this.getValidRecord(detailsResult);
      if (validRecord === null || validRecord === '') {
        updateAdditionalInfoFound({ isAdditionalInfoSecExist, hasAdditionalInfo: false });
      } else {
        const caseCountryMappingFormResult = await fetchCountryFormDetail(this.props.casePowerAppsId || detailsResult.case.caseId);
        if (caseCountryMappingFormResult.case) {
          caseCountryMappingFormResult.case.pageElements.forEach(item => (item.numberOfColumns = item.numberOfColumns === null ? null : 2));
        }
        this.qvDataLoaded = caseCountryMappingFormResult.case ? true : false;
        additionalInfoDetailUpdateInfo = {
          countryMappingFormDetails: caseCountryMappingFormResult,
          withInitValue: this.qvDataLoaded,
          regKey: validRecord,
        };
        updateAdditionalInfoData({
          isAdditionalInfoSecExist,
          hasAdditionalInfo: true,
          additionalInfoData: additionalInfoDetailUpdateInfo,
        });
      }
    } else {
      updateAdditionalInfoFound({ isAdditionalInfoSecExist: false, hasAdditionalInfo: false });
    }
    updateMainCaseInfoData(detailsResult);
    // enforcing two column display
    detailsResult.case.pageElements.forEach(item => (item.numberOfColumns = item.numberOfColumns === null ? null : 2));
    if (detailsResult) {
      this.setState({ formDetails: detailsResult });
    }
    setCaseLoading({ isLoading: false });
  };

  modifyDateFormat = value => {
    if (value) {
      if (value.match(DATE_REGEX.DATE_TIME_AM_PM_REGEX) || value.match(DATE_REGEX.DATE_ONLY_REGEX)) {
        return commonFormatDate(value);
      } else if (value.match(DATE_REGEX.DATE_ONLY_REGEX_2)) {
        return dateFormatEnUS(value);
      } else if (value.match(DATE_REGEX.DATE_TIME_REGEX)) {
        return formatDateAndTime(value);
      } else {
        return value;
      }
    }
  };

  updateCasePhase = phaseData => {
    const { caseId, casePowerAppsId } = this.props;
    const validCaseId = caseId || casePowerAppsId;
    const data = { validCaseId, casePhaseId: phaseData.id };

    this.props.updateCasePhase(data);
  };

  checkActionBar = () => {
    const { headerDetails } = this.props;
    const isActionBarHidden =
      headerDetails && headerDetails.currentCaseStatus && headerDetails.currentCaseStatus === 'Closed' ? true : false;
    return isActionBarHidden;
  };

  render() {
    const { caseClosed, isCorpClient, isCorpClientLineManager, isActive, isCaseLoading } = this.props;
    return (
      isActive && (
        <CasesFormDetailPres
          formDetails={this.state.formDetails}
          onFormChange={this.handleFormChange}
          casePhases={this.state.casePhases}
          showModal={this.state.showModal}
          onEdit={this.handleEdit}
          onCancel={this.toggleModal.bind(null, true)}
          onCancelModal={this.handleCancel}
          onConfirmModal={this.toggleModal.bind(null, false)}
          onSave={this.handleSave}
          isEditable={this.state.isEditable}
          isActionBarHidden={this.checkActionBar()}
          updateCasePhase={this.updateCasePhase}
          isCorpClient={isCorpClient || caseClosed}
          caseClosed={caseClosed}
          isCorpClientLineManager={isCorpClientLineManager}
          isLoading={this.state.isLoading || isCaseLoading}
        />
      )
    );
  }
}

const mapStateToProps = ({ cases: { casePhases } }) => ({
  casePhases,
});

const mapDispatchToProps = {
  updateCasePhase,
};

export default connect(mapStateToProps, mapDispatchToProps)(withSnackbar(CasesFormDetailContainer));
